/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useRef, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { ScrollCanvas } from 'components/scrollCanvas'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Waypoint } from 'react-waypoint'
import { useSpring, animated, useTransition, config } from '@react-spring/web'
import { ChainViewer } from 'components/chainViewer'
import { InlineMutation } from 'components/inlineMutation'
import { VariantsTable } from 'components/variantsTable'
import { ScrollViewPct } from 'components/scrollViewPct'
import { Link } from '@ta-interaktiv/contentbricks'

// TODO Set the correct article ID here
const ARTICLE_ID = '898476049541'
const isMobile = window.innerWidth < 768

export function AppComponent() {
  console.log('RENDER APP')

  const containerRef = useRef()

  const [stage, setStage] = useState({
    id: 1,
    direction: 'down',
    container: containerRef.current
  })

  const handleEnterWaypoint = (currentStageId, e, container) => {
    if (e.previousPosition === 'below') {
      console.log(currentStageId, 'down', container)

      setStage({
        id: currentStageId,
        direction: 'down',
        container: container
      })
    }
  }

  const handleLeaveWaypoint = (currentStageId, e, container) => {
    if (e.previousPosition === 'inside') {
      if (stage.id === currentStageId) {
        console.log(currentStageId, 'up', container)

        setStage({
          id: currentStageId - 1,
          direction: 'up',
          container: container
        })
      }
    }
  }

  const [{ chainViewerScrollPct }, apiTranslate] = useSpring(() => ({
    chainViewerScrollPct: 0
  }))

  const AnimatedChainViewer = animated(ChainViewer)

  const SizeTransition = useTransition(stage.id === 4, {
    config: config.stiff,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  const animatedChainViewerTransition = useTransition(stage.id === 9, {
    config: config.stiff,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  return (
    <div className='index'>
      <Masthead inverted articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <header>
        <div className='ui vertical very fitted inverted segment'>
          <div className='ui text center aligned container'>
            <div className='cover-text'>
              <div className='ui header'>
                <div className='top-line'>Immersion en 3D</div>
                <h1>Les mutations du Covid-19</h1>
                <h2>
                  Découvrez les mécanismes qui rendent le coronavirus plus ou
                  moins vulnérable aux vaccins dans une représentation détaillée
                  et inédite de sa structure clé: la protéine Spike.
                </h2>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className='scene-container'>
        <ScrollCanvas stage={stage} />
        <div className='overlay'>
          {SizeTransition(
            (styles, item) =>
              item && (
                <animated.div
                  style={{
                    opacity: styles.opacity.to([0, 1], [0, 1])
                  }}
                  className='size-overlay'
                >
                  <div className='ruler' />
                  <div className='size-label'>
                    <div>20 nanomètres</div>
                    {/* <div><small>50'000 fois plus petit qu'un grain de sel</small></div> */}
                  </div>
                </animated.div>
              )
          )}
          {animatedChainViewerTransition(
            (styles, item) =>
              item && (
                <animated.div
                  style={{
                    opacity: styles.opacity.to([0, 1], [0, 1])
                  }}
                >
                  <AnimatedChainViewer />
                </animated.div>
              )
          )}
        </div>
        <div className='stages-container'>
          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(1, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(1, e)
            }}
          >
            <div
              ref={containerRef}
              className='scroll-text'
              style={{ paddingTop: '0vh', paddingBottom: '20vh' }}
            >
              <div className='ui text container'>
                <div className='ta metainfo'>
                  <div className='ta author'>
                    <b>Mathieu Rudaz</b>
                  </div>
                  <div className='ta updated'>Mis à jour le 3 mai 2022</div>
                </div>
                <div className='ui ta text'>
                  <p>
                    Le nombre d’infections au Covid-19 continue de diminuer en
                    Suisse, mais il en va tout autrement dans d’autres pays
                    exposés à de nouvelles sous-lignées du variant Omicron.
                    L’Afrique du Sud et les États-Unis ont vu leur taux
                    d’infection grimper ces dernières semaines. La communauté
                    scientifique s'inquiète. Il y a quelques jours, le
                    professeur berlinois Christian Dorsten, l’un des
                    virologistes les plus respectés au monde, estimait que la
                    situation restait{' '}
                    <Link href='https://twitter.com/c_drosten/status/1519308297760825346'>
                      préoccupante
                    </Link>
                    . Particulièrement en raison d’une mutation sur le résidu
                    452 de la protéine Spike, qui augmenterait la virulence du
                    virus.
                  </p>
                  <p>
                    Alors qu’est-ce que cela veut dire exactement? Et qu’est-ce
                    que cette fameuse protéine en forme de minuscule champignon
                    dont est hérissée la surface du coronavirus? Cette
                    excroissance, encore pleine de secrets, joue un rôle
                    essentiel dans la propagation du Covid-19.
                  </p>
                  <p>
                    Car c’est <Link href='912428417836'>ce fragment</Link> du
                    virus qui permet à notre système immunitaire de repérer le
                    virus et de purger notre corps de celui-ci. C’est aussi sur
                    lui que reposent les{' '}
                    <Link href='202685556480'>vaccins ARNm</Link>, qui
                    représentent la quasi-totalité des doses administrées en
                    Suisse. Voilà pourquoi les mutations dans cette protéine
                    sont si importantes.
                  </p>
                  <p>
                    B.2.1.12, BA.4, BA.5, les nouveaux variants ne cessent de se
                    multiplier. C’est pour comprendre ce que ces acronymes
                    représentent que nous vous proposons une plongée au cœur de
                    la protéine Spike.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(2, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(2, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              />
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(3, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(3, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Souvent réduites à un cercle entouré de protubérances, les
                    représentation du Covid-19 font maintenant partie du
                    paysage. Ce sont ces pics qui donnent leur nom à la
                    sous-famille des coronavirus, en latin «virus à couronne».
                  </p>
                  <p>
                    Les protéines Spike sont essentielles au SARS-CoV2 puisque
                    ce sont elles qui arriment puis fusionnent le virus avec les
                    cellules de notre corps pour lui permettre de se répliquer.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(4, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(4, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Voilà donc en quelque sorte le symbole de la maladie: bien
                    qu’elle ne soit pas la seule protéine à la surface de
                    l'enveloppe du virus, Spike est la principale cible des
                    anticorps. C'est elle qui permet à nos défenses imunitaires
                    d'identifier le virus.
                  </p>
                  <p>
                    Sans surprise, elle accapare donc l'attention des
                    chercheuses et chercheurs. Fin 2021, grâce à l’un des
                    microscopes électroniques les plus puissants du monde, une{' '}
                    <Link href='229901868369'>équipe de l'EPFL</Link> en a
                    d'ailleurs obtenu l'une des représentations les plus
                    détaillées, à l’échelle de l’atome.
                  </p>
                  <p>
                    C’est très important, car les scientifiques ont par exemple
                    pu observer avec précision quelles modifications peuvent
                    expliquer pourquoi «Omicron échappe totalement au vaccin
                    d’AstraZeneca, et partiellement à celui de Pfizer.»
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(5, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(5, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    La structure de la protéine Spike est faite de trois
                    éléments identiques, ou chaînes, elles-mêmes constituées
                    d'un assemblage d'acides aminés. Nous parlerons de ces
                    derniers un peu plus tard.
                  </p>
                  <p>
                    Chacune de ces chaînes peuvent être représentées de
                    nombreuses manières dont voici trois exemples communément
                    utilisés.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(6, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(6, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Ces façons de présenter la protéine mettent chacune en
                    évidence un aspect particulier de sa structure.
                  </p>
                  <p>
                    C'est cette première image qui est généralement utilisée
                    dans les médias. Elle montre l'enveloppe formée par les
                    acides aminés.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(7, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(7, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Car ce sont les acides aminés - il en existe vingt - qui
                    constituent les protéines du corps humain.
                  </p>
                  <p>
                    Ces briques élémentaires du vivant se lient entre elles afin
                    de former une chaîne, qui peut s’associer à d’autres, et
                    prendre une forme qui lui est spécifique. Ces formes vont
                    permettre de déterminer les propriétés biologiques de chaque
                    protéine.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(8, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(8, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Pour le comprendre, nous allons suivre un court segment
                    d'une chaîne protéique. Chacun des acides aminés qui la
                    composent est ici représenté avec son abréviation et sa
                    position dans la chaîne. La place et la nature de chaque
                    acide aminé sont déterminées par l'ARN du virus.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(9, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(9, e)
            }}
          >
            <div
              className='scroll-text'
              style={{
                paddingTop: '0vh',
                paddingBottom: '0vh',
                height: '200vh'
              }}
            >
              <ScrollViewPct
                fromTop
                // onScroll={value => {
                //   apiTranslate.start({
                //     chainViewerScrollPct: value
                //   })
                // }}
              >
                <div style={{ height: '100%' }} />
              </ScrollViewPct>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(10, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(10, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Seulement, au fil des contaminations et des réplications du
                    virus, l'<b>ARN</b> de certaines de ses copies peut
                    légèrement différer de l'original, modifiant ainsi ses
                    propriétés. Un acide aminé peut alors être remplacé par un
                    autre ou encore, être purement et simplement supprimé.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(11, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(11, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Dans le cas d'Omicron, pas moins de <b>36 mutations</b> ont
                    été détectées sur la protéine Spike. Bien plus que celles du
                    variant Delta.
                  </p>
                  <p>
                    Pour faire état de ces modifications dans les chaînes
                    protéiques, les scientifiques utilisent une notation
                    particulière qui indique où et quel acide aminé a changé.
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1em',
                      marginBottom: '0.65em',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      style={{ width: '100%', maxWidth: '300px' }}
                      src={require('../../images/mutation-desktop.svg')}
                    />
                    {/* <div style={{position: 'relative'}}>
                      <div className='inlinemutation-label label-a'>
                      Sur la protéine spike
                      </div>
                      <div className='inlinemutation-label label-b'>
                      à la position 60 de la chaîne
                        protéique
</div>
<div className='inlinemutation-label label-c'>
l'acide arginine devient lysine
</div>
<div className='inlinemutation-label label-d'>
l'acide arginine devient lysine
</div>
                      <InlineMutation protein='s' from='A' pos={60} to='L' />
                    </div> */}
                    {/* <div style={{ marginLeft: '0.65em' }}>
                      <small>
                        Sur la protéine spike (S), à la position 60 de la chaîne
                        protéique, l'acide arginine devient lysine.
                      </small>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </Waypoint>

          {/* Et c'est la que les problèmes apparaissent, expliquer ACE2 et système immunitaire */}

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(12, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(12, e)
            }}
          >
            <div className='chart-container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div
                  className='scroll-text-content'
                  style={{
                    backgroundColor: 'transparent',
                    backdropFilter: 'none'
                  }}
                >
                  <div
                    className='ui text container'
                    style={{ marginBottom: '1em' }}
                  >
                    <h4 style={{ marginBottom: '0.25em' }}>
                      Les mutations des variants Delta et Omicron sur la
                      protéine Spike par rapport au virus originel (Wuhan)
                    </h4>
                    <p>
                      Un − indique que l'acide aminé a disparu de cette
                      position.
                    </p>
                  </div>
                  <div className='ui container'>
                    <VariantsTable />
                  </div>
                  <div className='ui text container'>
                    <div className='source'>Source: Covariants.org</div>
                  </div>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(13, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(13, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Or ce sont justement les acides aminés qui permettent à
                    notre système immunitaire d'identifier la protéine grâce aux
                    anticorps neutralisants.
                  </p>
                  <p>
                    Ces derniers ciblent différentes parties de la protéine, en
                    particulier la région qui interagit avec nos cellules. En
                    l’absence d’anticorps, cette zone, située au sommet de la
                    protéine Spike, permet au virus de s'amarrer et de fusionner
                    avec nos cellules.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(14, e)
            }}
            onLeave={e => {
              handleLeaveWaypoint(14, e)
            }}
          >
            <div className='ui text container'>
              <div
                className='scroll-text'
                style={{ paddingTop: '0vh', paddingBottom: '100vh' }}
              >
                <div className='scroll-text-content'>
                  <p>
                    Mais pour y parvenir, les anticorps doivent encore percer un
                    «bouclier de sucre» dont est couverte Spike. Ces molécules
                    de sucre permettent, entre autres, au virus de se camoufler.
                  </p>
                  <p>
                    C'est pourquoi les mutations sur cette zone de la protéine
                    sont les plus critiques puisque que celle-ci est découverte
                    au moment de son amarrage avec une cellule.
                  </p>
                </div>
              </div>
            </div>
          </Waypoint>

          <Waypoint
            onEnter={e => {
              handleEnterWaypoint(15, e)
            }}
            onLeave={e => {
              if (e.currentPosition === 'below') {
                handleLeaveWaypoint(15, e)
              }
            }}
          >
            <div
              className='scroll-text'
              style={{ paddingTop: '0vh', paddingBottom: '4em' }}
            >
              <div className='ui ta text container'>
                <p>
                  La protéine Spike et ses nombreuses mutations expliquent donc
                  pourquoi le variant Omicron a continué de progresser, même
                  dans les populations fortement vaccinées. Cette variante du
                  virus est devenue plus difficile à détecter par notre système
                  immunitaire. Elle a rempli le vide laissé par le variant Delta
                  qui, lui, a déjà été affaibli par la vaccination et l'immunité
                  collective. Puis elle l'a supplanté.
                </p>
                <p>
                  En février 2022, pas moins de{' '}
                  <Link href='https://www.covid19.admin.ch/fr/epidemiologic/virus-variants'>
                    12 variants d'intérêt
                  </Link>
                  , faisant l’objet d’une surveillance particulière, ont été
                  répertoriés par l’Office fédéral de la santé publique. Les
                  liens de parenté de ces variants peuvent être représentés sous
                  la forme d'une sorte d’arbre généalogique: un arbre
                  phylogénétique, comme disent les scientifiques.
                </p>
              </div>
              <div className='ui vertical segment chart-container'>
                <div className='ui text container'>
                  <h4>Vue générale des relations entre principaux variants</h4>
                </div>
                <div className='ui container'>
                  <img
                    src={require(`../../images/family-tree-${
                      isMobile ? 'mobile' : 'desktop'
                    }.svg`)}
                  />
                </div>
                <div className='ui ta text container'>
                  <div className='source'>Source: Covariants.org</div>
                </div>
              </div>
              <div className='ui ta text container'>
                <p>
                  La pression sélective, qui favorise la propagation d'un
                  nouveau variant dans la population au détriment d'un autre,
                  peut venir de nombreux facteurs. Le nombre de personnes ayant
                  déjà été infectées et la vaccination en font partie. Un vaccin
                  qui cible la protéine Spike d'un certain variant peut, par
                  exemple, favoriser l'émergence d'un autre.
                </p>
                <p>
                  De nouveaux variants continuent donc d'apparaître.
                  Aujourd'hui, on parle même de «recombinants», qui naissent
                  lorsqu'une personne est infectée par plusieurs variants
                  différents. L'incidence et la prévalence de la maladie
                  devraient cependant diminuer. Et puisque le virus sera moins
                  répandu dans la population, la rapidité d’apparition de
                  nouveaux variants devrait en faire autant.
                </p>

                <p>
                  En attendant, les scientifiques suivent de près l'évolution
                  des mutations dans le génome du virus.
                </p>
              </div>
            </div>
          </Waypoint>
        </div>
      </div>

      <div style={{ zIndex: 2, position: 'relative' }}>
        <div className='ui vertical black inverted segment method'>
          <div className='ui container'>
            <div className='ui ta text container'>
              <h3>Méthodologie</h3>
              <p>
                La protéine Spike a été modélisée à partir{' '}
                <Link href='https://www.rcsb.org/structure/6VXX'>
                  des informations
                </Link>{' '}
                obtenues depuis la banque de données sur les protéines (BDP) du
                Research Collaboratory for Structural Bioinformatics. Cette
                collection, aussi appelée Protein Data Bank, ou PDB, est le
                fruit d'une collaboration mondiale qui vise à acquérir des
                données sur la structure du vivant. Sa visualisation est
                inspirée d'un{' '}
                <Link href='https://entagma.com/guest-tutorial-dr-jeroen-claus-rendering-the-corona-virus-spike-protein'>
                  guide
                </Link>{' '}
                du Dr Jeroen Claus.
              </p>
              <p>
                Les sources des données et graphiques sur les variants
                proviennent du site{' '}
                <Link href='https://covariants.org/'>CoVariants.org</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer
        inverted
        locale='fr'
        hidePortfolioTeaser
        articleId={ARTICLE_ID}
        mailTo='interactif@tamedia.ch'
        credits={[
          ['Conception et réalisation', 'Mathieu Rudaz'],
          [
            'Remerciements',
            'Anna Anchimuk pour ses précieux conseils scientifiques'
          ]
        ]}
      />
    </div>
  )
}
